import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EnquiryForm } from "@/components/enquiry-form";
import { cn } from "@/lib/utils";

/**
 * Form dropdown values (from SERVICES.shortName):
 * - Grant CTA maps to "Air Source Heat Pumps"
 * - Solar CTA maps to "Solar PV"
 */
const HEAT_PUMP_SERVICE = "Air Source Heat Pumps";
const SOLAR_SERVICE = "Solar PV";

type MagnetKey = "heat-pump" | "solar";

const MAGNETS: Record<
  MagnetKey,
  {
    headline: string;
    supporting: string;
    cta: string;
    stickyHeadline: string;
    stickyCue: string;
    stickyLabel: string;
    ctaVariant: "accent" | "navy";
    stickyVariant: "accent" | "navy";
    linkLabel: string;
    linkTo: "/boiler-upgrade-scheme" | "/solar-pv";
    microcopy: string;
    service: string;
  }
> = {
  "heat-pump": {
    headline: "Could Your Home Qualify For A £9,000 Heat Pump Grant?",
    supporting: "Check in around 60 seconds. No obligation.",
    cta: "Check If I Qualify",
    stickyHeadline: "Could you get a £9,000 heat pump grant?",
    stickyCue: "Free Boiler Upgrade Scheme Eligibility Check →",
    stickyLabel: "Boiler Upgrade Scheme",
    ctaVariant: "accent",
    stickyVariant: "navy",
    linkLabel: "Boiler Upgrade Scheme",
    linkTo: "/boiler-upgrade-scheme",
    microcopy:
      "Grant funding is subject to eligibility, property suitability and survey findings.",
    service: HEAT_PUMP_SERVICE,
  },
  solar: {
    headline: "Find Out How Much Energy Your Roof Could Generate",
    supporting: "Free roof assessment with clear numbers, in writing.",
    cta: "Book A Free Roof Assessment",
    stickyHeadline: "Get a Free Solar Estimate →",
    stickyCue: "Find out what your roof could generate - no home visit needed",
    stickyLabel: "Get a Free Solar Estimate",
    ctaVariant: "navy",
    stickyVariant: "accent",
    linkLabel: "Solar PV",
    linkTo: "/solar-pv",
    microcopy:
      "Generation estimates are based on an MCS performance estimate in your written proposal.",
    service: SOLAR_SERVICE,
  },
};

function MagnetEnquiryDialog({
  openKey,
  onOpenChange,
}: {
  openKey: MagnetKey | null;
  onOpenChange: (open: boolean) => void;
}) {
  const openMagnet = openKey ? MAGNETS[openKey] : null;

  return (
    <Dialog open={openKey !== null} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md sm:max-w-lg p-0 overflow-y-auto max-h-[calc(100dvh-2rem)] bg-transparent border-0 shadow-none rounded-2xl">
        {openMagnet && (
          <>
            <DialogHeader className="sr-only">
              <DialogTitle>{openMagnet.cta}</DialogTitle>
              <DialogDescription>
                Send us your details and we&apos;ll arrange a free, no-obligation assessment for{" "}
                {openMagnet.service}.
              </DialogDescription>
            </DialogHeader>
            <EnquiryForm
              formType="survey"
              variant="survey"
              defaultService={openMagnet.service}
              sourcePage="/"
              title={openMagnet.cta}
              subtitle="Free assessment, no obligation. We'll be in touch shortly."
              submitLabel="Send enquiry"
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Coloured lead-magnet banners under Reviews. Desktop: one joined 50/50 bar.
 *  Each half links straight to the relevant service landing page.
 *  Shown site-wide via SiteHeader on all pages with default chrome. */
export function HomeLeadMagnetStickyBar() {
  return (
    <div className="flex flex-row border-b border-border">
      {(["solar", "heat-pump"] as const).map((key) => {
        const m = MAGNETS[key];
        const shortLabel =
          key === "solar" ? "Free Solar Estimate →" : "£9,000 Heat Pump Grant →";
        return (
          <Link
            key={key}
            to={m.linkTo}
            className={cn(
              "w-1/2 min-w-0 text-left active:opacity-90 transition-opacity",
              m.stickyVariant === "accent"
                ? "bg-accent text-accent-foreground"
                : "bg-primary text-primary-foreground"
            )}
          >
            {/* Mobile: single-line minimised label */}
            <span className="md:hidden block truncate px-3 py-2 text-xs font-bold leading-snug">
              {shortLabel}
            </span>
            {/* Desktop: two-line richer copy */}
            <span className="hidden md:flex flex-col gap-0.5 px-4 sm:px-6 lg:px-8 py-2.5">
              <span className="text-sm font-bold leading-snug truncate">
                {m.stickyHeadline}
              </span>
              <span className="text-xs font-medium leading-snug opacity-90 truncate">
                {m.stickyCue}
              </span>
            </span>
          </Link>
        );
      })}
    </div>
  );
}



/** Mid-page equal dual banner (under reviews carousel). */
export function HomeLeadMagnets() {
  const [openKey, setOpenKey] = useState<MagnetKey | null>(null);

  return (
    <>
      <section className="bg-secondary py-10 md:py-12">
        <div className="container-page">
          <div className="grid md:grid-cols-2 md:divide-x md:divide-border">
            {(["heat-pump", "solar"] as const).map((key, index) => {
              const m = MAGNETS[key];
              return (
                <div
                  key={key}
                  className={cn(
                    "flex flex-col py-8 md:py-2 md:px-8 first:pt-0 last:pb-0 md:first:pl-0 md:last:pr-0",
                    index > 0 && "border-t border-border md:border-t-0"
                  )}
                >
                  <h2 className="text-xl md:text-2xl font-bold text-balance leading-snug">
                    {m.headline}
                  </h2>
                  <p className="mt-2 text-muted-foreground text-pretty">{m.supporting}</p>

                  <div className="mt-5 flex flex-col gap-2.5">
                    <button
                      type="button"
                      onClick={() => setOpenKey(key)}
                      className={
                        m.ctaVariant === "accent"
                          ? "inline-flex w-full sm:w-auto sm:self-start items-center justify-center rounded-full bg-accent text-accent-foreground px-6 py-3 font-semibold shadow-cta hover:bg-accent/90 transition-colors"
                          : "inline-flex w-full sm:w-auto sm:self-start items-center justify-center rounded-full bg-primary text-primary-foreground px-6 py-3 font-semibold shadow-soft hover:bg-primary/90 transition-colors"
                      }
                    >
                      {m.cta}
                    </button>
                    <p className="text-sm text-muted-foreground">
                      Learn more on our{" "}
                      <Link
                        to={m.linkTo}
                        className="font-semibold text-accent hover:underline"
                      >
                        {m.linkLabel}
                      </Link>{" "}
                      page →
                    </p>
                  </div>

                  <p className="mt-4 text-xs text-muted-foreground leading-relaxed">
                    {m.microcopy}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <MagnetEnquiryDialog openKey={openKey} onOpenChange={(open) => !open && setOpenKey(null)} />
    </>
  );
}
